import { Component, computed, effect, inject, output, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SidePanelService } from '../../../services/side-panel.service';
import { GeneralAgentService, IFileVersion } from '../../../services/general-agent.service';
import { ToastService } from 'src/app/@core/services/toast.service';
import { SelectComponent } from 'src/app/@core/components/ui/select/select.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component'; // Verify import path
import { MultiFormatPreviewComponent } from 'src/app/@shared/components/multi-format-preview/multi-format-preview.component';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
import { GeotechnicalAnalysisService } from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';

import { TooltipModule } from 'primeng/tooltip';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { SelectConfig, SelectOption } from 'src/app/@core/components/ui/select/select.model';
// import { IAttachment } from 'src/app/models/model'; // Check if IAttachment is needed/where it is defined. Assuming IAttachment interface exists.

// Interfaces if not imported
interface IAttachment {
    _id?: string;
    filename?: string;
    url?: string;
    filePath?: string;
    type?: string;
    fullUrl?: string; // used in download
    isVersioned?: boolean;
    // ...
}

@Component({
    selector: 'app-file-preview',
    standalone: true,
    imports: [
        CommonModule,
        SelectComponent,
        IconComponent,
        MultiFormatPreviewComponent,
        DialogComponent,
        TooltipModule
    ],
    templateUrl: './file-preview.component.html',
    styleUrl: './file-preview.component.scss'
})
export class FilePreviewComponent {
    readonly closePreview = output<void>();
    sidePanelService = inject(SidePanelService);
    generalAgentService = inject(GeneralAgentService);
    toastService = inject(ToastService);
    queryClient = inject(QueryClient);
    geoReportDataService = inject(GeoTechnicalAnalysisReportDataService);
    geotechnicalAnalysisService = inject(GeotechnicalAnalysisService);

    @ViewChild('fileVersionSelect') fileVersionSelect?: SelectComponent;

    // Local State
    fileLoading = signal<boolean>(false);
    selectedDocumentFile = signal<any>(null); // Use any or IAttachment if imports allow
    selectedDocumentId = signal<string>('');

    // File Versions
    fileVersionsOptions = signal<SelectOption[]>([]);
    selectedFileVersion = signal<string>('');
    fileVersionsConfig: SelectConfig = {
        placeholder: 'Select a file version',
        searchable: false,
        clearable: false,
        styleClass: 'border',
        appendTo: 'body'
    };

    // Fullscreen
    isDocumentFullscreenOpen = signal<boolean>(false);

    // Publish as AI Dashboard
    publishingDashboard = signal<boolean>(false);

    /** True when the currently previewed file is an HTML file. */
    isHtmlFile = computed(() => {
        const file = this.selectedDocumentFile();
        if (!file) return false;
        const type: string = file.type ?? '';
        const name: string = file.filename ?? '';
        return type === 'html' || type === 'text/html' || name.toLowerCase().endsWith('.html');
    });

    /**
     * True when:
     *   - we are inside a geotechnical analysis context
     *   - the current user is an Owner or Editor
     *   - the previewed file is an HTML file
     */
    canPublishAsDashboard = computed(() =>
        !!(
            this.geoReportDataService.analysisReportId() &&
            (this.geoReportDataService.isOwner() || this.geoReportDataService.isEditor()) &&
            this.isHtmlFile()
        )
    );

    // Icons
    constructor() {
        effect(() => {
            const context = this.sidePanelService.context();
            if (context?.file) {
                const file = context.file;
                // Only load if different or initial
                if (this.selectedDocumentId() !== file._id) {
                    this.selectedDocumentId.set(file._id);
                    console.log(file)
                    this.selectedDocumentFile.set(file);
                    this.loadFile(file);
                }
            } else {
                this.selectedDocumentId.set('');
                this.selectedDocumentFile.set(null);
            }
        }, { allowSignalWrites: true });
    }

    private async loadFile(file: any): Promise<void> {
        this.fileLoading.set(true);
        // Reset versions
        this.fileVersionsOptions.set([]);
        this.selectedFileVersion.set('');

        if (file.isVersioned && (file.url || file.filePath)) {
            try {
                const filePath = file.url || file.filePath || '';

                const versionsRes = await this.queryClient.fetchQuery({
                    queryKey: ['file-versions', filePath],
                    queryFn: () => this.generalAgentService.getFileVersions(filePath),
                }) as { success: boolean, data: IFileVersion[] };

                if (versionsRes?.success && versionsRes?.data) {
                    this.fileVersionsOptions.set(versionsRes.data.map(version => ({
                        label: version.name,
                        value: version.versionId
                    })));

                    if (this.fileVersionsOptions().length > 0) {
                        const targetVersionId = this.fileVersionsOptions()[0].value;
                        this.selectedFileVersion.set(targetVersionId);
                        await this.loadFileVersionUrl(file, targetVersionId);
                    }
                }
            } catch (error) {
                console.error('Error loading file versions:', error);
                // Continue showing valid initial file if version load fails?
            }
        }
        this.fileLoading.set(false);
    }

    private async loadFileVersionUrl(file: any, versionId: string): Promise<void> {
        const filePath = file.url || file.filePath || '';
        try {
            const versionUrlRes = await this.queryClient.fetchQuery({
                queryKey: ['selected-version-url', filePath, versionId],
                queryFn: () => this.generalAgentService.getSelectedVersionUrl(filePath, versionId),
            }) as { success: boolean, data: string };

            if (versionUrlRes?.success && versionUrlRes?.data) {
                if (file && versionUrlRes.data !== file.fullUrl) {
                    this.selectedDocumentFile.set({
                        ...file,
                        fullUrl: versionUrlRes.data,
                        url: versionUrlRes.data // Update display URL if needed
                    });
                }
            }
        } catch (error) {
            console.error('Error loading version URL:', error);
        }
    }

    async onFileVersionChange(versionId: string) {
        if (!versionId) return;
        this.selectedFileVersion.set(versionId);
        this.fileLoading.set(true);
        const file = this.selectedDocumentFile();
        // Need base file structure, assuming selectedDocumentFile has base info
        await this.loadFileVersionUrl(file, versionId);
        this.fileLoading.set(false);
    }

    openFileVersionDropdown(event: Event) {
        event.stopPropagation();
        this.fileVersionSelect?.selectComponent?.show();
    }

    openDocumentFullscreen() {
        this.isDocumentFullscreenOpen.set(true);
    }

    closeDocumentFullscreen() {
        this.isDocumentFullscreenOpen.set(false);
    }

    closeDocumentPreview() {
        this.sidePanelService.close();
        this.closePreview.emit();
    }

    downloadDocument(): void {
        const file = this.selectedDocumentFile();
        if (!file) {
            this.toastService.error('No document selected');
            return;
        }
        const fileUrl = file.fullUrl || file.url; // Fallback
        if (!fileUrl) {
            this.toastService.error('Document URL not available');
            return;
        }
        fetch(fileUrl, {
            method: 'GET',
            mode: 'cors',
            cache: 'no-cache'
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch document');
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.filename || 'document';
                link.click();
                window.URL.revokeObjectURL(url);
                this.toastService.success('Document downloaded successfully');
            })
            .catch(err => {
                console.error('Download error:', err);
                this.toastService.error('Failed to download document');
            });
    }

    pageNumber = computed(() => {
        const ctx = this.sidePanelService.context();
        return ctx?.source?.page_number ?? 1;
    });

    documentPreviewStyles = computed(() => {
        return {
            width: this.isDocumentFullscreenOpen() ? '100vw' : '100%',
            height: this.isDocumentFullscreenOpen() ? '100vh' : '100%',
            maxWidth: '100vw',
            maxHeight: this.isDocumentFullscreenOpen() ? '100vh' : '100%'
        };
    });

    onPublishDashboard(): void {
        const analysisId = this.geoReportDataService.analysisReportId();
        const attachmentId = this.selectedDocumentFile()?._id;
        if (!analysisId || !attachmentId || this.publishingDashboard()) return;

        this.publishingDashboard.set(true);
        this.geotechnicalAnalysisService.importChatHtmlAsDashboard(analysisId, attachmentId).subscribe({
            next: () => {
                this.toastService.success('Published to AI Dashboard successfully');
                this.publishingDashboard.set(false);
            },
            error: () => {
                this.toastService.error('Failed to publish to AI Dashboard');
                this.publishingDashboard.set(false);
            },
        });
    }
}
